/**
 * DetailCard — the ONE card behind every expense and map event (Pass 3: the
 * ledger + the detail card), now with THREE TIERS OF DISCLOSURE so it opens at
 * about a third of its old height with every number you need still on screen:
 *   1. OPEN — "Record what you spent": above each stepper, a violet card reads
 *      the formula back ("31 units of R200 = R6 200 this month · each ± step
 *      pays R200"; a map event "R24,00 a day, 2 visits · logs on top of the
 *      units above"; spend-only "no formula — whatever you log is what it
 *      cost"). Each violet sits directly above the stepper it explains.
 *   2. COLLAPSED — "Set up as: TymeBank · Food & Drink · 📍 place · 2× a day ·
 *      not tax-deductible" + Change. Bank/category/place/cadence/tax are filing,
 *      not mechanics. Inside, one further More options for the rare controls
 *      (set total, budget count, merge, recount, delete).
 *   3. COLLAPSED — "Logged this month · 4 entries · R 251,00 · 2 with a receipt
 *      · last 2 Aug" + Show → the fixed 196px scroll, a receipt button per line.
 * A map event is an expense with a place and a cadence — the same card.
 * Every spent figure comes from spentOf()/leftOf() in money.js, so the card,
 * the rows and the section headers can never disagree.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { taxPackAvailable } from '../global.js';
import { fmtR, unitRate, spentOf, leftOf, spendBreakdown } from '../money.js';
import VoiceButton from './VoiceButton.jsx';

const BUCKETS = [['travel', 'Travel'], ['home_office', 'Home office'], ['donations', 'Donations'],
  ['medical', 'Medical'], ['retirement', 'Retirement'], ['business', 'Business']];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Units in the formula = the first product term's value ÷ its unit rate, so
// 31*2/6*80 reads 10,3 units of R80 (R826,67), not "2 units" (the smallest
// number). 200*31 → 31, 600*2+1700 → 2 (the flat R1700 is not units).
function countOf(calc) {
  const term = String(calc || '').replace(/×/g, '*').split('+').find((t) => t.includes('*'));
  const rate = unitRate(calc);
  if (!term || !rate) return 0;
  const tokens = term.split('-')[0].replace(/\s/g, '').match(/[*/]|\d+(?:\.\d+)?/g) || [];
  let v = null; let op = '*';
  for (const t of tokens) {
    if (t === '*' || t === '/') { op = t; continue; }
    const n = Number(t);
    v = v === null ? n : (op === '*' ? v * n : v / n);
  }
  return v ? Math.round((v / rate) * 10) / 10 : 0;
}
// Unit counts read 31 or 10,3 (one decimal only when the formula has one).
const fmtCount = (n) => Number(n || 0).toLocaleString('en-ZA', { maximumFractionDigits: 1 });
// Compact rand for the read-back lines: R200, R6 200 (no ",00" when whole).
function rc(n) {
  const v = Math.round(Number(n) || 0);
  return 'R' + String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
function shortDate(s) {
  if (!s) return '';
  const p = String(s).slice(0, 10).split('-');
  return p.length === 3 ? `${Number(p[2])} ${MON[Number(p[1]) - 1] || ''}` : '';
}

export default function DetailCard({
  item, categories = [], banks = [], items = [],
  onChange, onScan, onOpenMap, onTick, onClose, variant = 'inline', showHeader = true,
}) {
  const [spendVal, setSpendVal] = useState('');
  const [totalVal, setTotalVal] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [loggedOpen, setLoggedOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [visits, setVisits] = useState([]);
  const [nonce, setNonce] = useState(0);
  const [event, setEvent] = useState(null);
  const [recount, setRecount] = useState(null);
  const [mergeTo, setMergeTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [recurOpen, setRecurOpen] = useState(false);   // make-recurring panel (existing expense)
  const [rcad, setRcad] = useState('none');            // none | daily | weekly | monthly
  const [rUnit, setRUnit] = useState('');
  const [rTimes, setRTimes] = useState('1');
  const [rDow, setRDow] = useState([]);
  const [rDay, setRDay] = useState('');
  const [recurBusy, setRecurBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api(`/budget/place-visits/?item=${item.id}`)   // per-item, cycle-independent — never ?month=
      .then((v) => { if (live) setVisits(Array.isArray(v) ? v : (v?.results || [])); })
      .catch(() => {});
    return () => { live = false; };
  }, [item.id, nonce]);

  useEffect(() => {
    let live = true;
    api('/budget/place-visit-rules/')
      .then((rs) => {
        const list = Array.isArray(rs) ? rs : (rs?.results || []);
        const ev = list.find((r) => r.active && (r.item_title || '').toLowerCase() === (item.title || '').toLowerCase());
        if (live) setEvent(ev || null);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [item.id, item.title, nonce]);

  const refresh = () => { setNonce((n) => n + 1); onChange?.(); };
  // #715 — paid twice by mistake: next month's copy is marked paid, no money
  // moves next month (it already left this month). On a future month's line it
  // marks that line itself. "Undo paid ahead" reverses it.
  const [aheadMsg, setAheadMsg] = useState('');
  const isPaidAhead = !!(item.completed && String(item.note || '').startsWith('Paid ahead'));
  const payAhead = async (undo) => {
    setBusy(true); setAheadMsg('');
    try {
      const d = await api(`/budget/items/${item.id}/pay_ahead/`, { method: 'POST', body: undo ? { undo: true } : {} });
      const mName = d?.month ? new Date(`${d.month}-01T12:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' }) : 'next month';
      setAheadMsg(undo ? 'Paid ahead undone.' : `Marked paid for ${mName} — nothing more leaves your bank then.`);
    } catch { setAheadMsg('Could not mark it — try again.'); }
    setBusy(false); refresh();
  };
  const isRecurring = !!item.template;
  // Promote an EXISTING expense into the recurring setup, optionally with an
  // advanced-repeat cadence — the same contract the add sheet uses.
  const makeRecurring = async () => {
    setRecurBusy(true);
    try {
      const body = { cadence: rcad };
      if (rcad !== 'none') {
        if (rUnit !== '') body.unit_rate = rUnit;
        if (rcad === 'daily') body.times_per_day = Number(rTimes) || 1;
        if (rcad === 'weekly') body.days_of_week = rDow.slice().sort((a, b) => a - b).join(',');
        if (rcad === 'monthly' && rDay !== '') body.expected_day = Number(rDay) || '';
      }
      await api(`/budget/items/${item.id}/make_recurring/`, { method: 'POST', body });
      setRecurOpen(false);
    } catch { /* */ }
    setRecurBusy(false); refresh();
  };
  const isExpense = item.kind === 'expense';
  const formula = !!item.calculation;
  const rate = unitRate(item.calculation);
  const count = countOf(item.calculation);
  const spent = spentOf(item);
  const budget = Number(item.amount) || 0;
  const left = leftOf(item);
  const over = spent > budget && budget > 0;

  // Map event (rule) + its stepper figures.
  const eventVisits = event ? visits.filter((v) => String(v.rule) === String(event.id)) : [];
  const today = new Date().toISOString().slice(0, 10);
  const evtToday = eventVisits.filter((v) => v.on === today).length;
  const evtTimes = event && event.cadence === 'daily' ? Math.max(1, Number(event.times_per_day) || 1) : 1;
  const evtPerVisit = event ? (event.units ? (Number(event.units) || 0) * rate : Number(event.amount) || 0) : 0;

  // Logged-this-month summary.
  const visMoney = (v) => (v.units > 0 ? v.units * rate : Number(v.amount) || 0);
  const logTotal = visits.reduce((a, v) => a + visMoney(v), 0);
  const eventLogged = eventVisits.reduce((a, v) => a + visMoney(v), 0);
  const receiptCount = visits.filter((v) => v.receipt).length;
  const lastOn = visits.map((v) => v.on).filter(Boolean).sort().slice(-1)[0];

  // Set-up-as summary line.
  const bankName = banks.find((b) => String(b.id) === String(item.bank))?.name || item.bank_name || '—';
  const catName = categories.find((c) => String(c.id) === String(item.category))?.name || item.category_name || '—';
  const placeName = item.place_name || (event && event.place_name) || null;
  const cadenceStr = event ? (evtTimes > 1 ? `${evtTimes}× a day` : (event.cadence || 'daily')) : null;
  const setupLine = ['Set up as: ' + [bankName, catName].filter((x) => x && x !== '—').join(' · ')
    + (placeName ? ` · 📍 ${placeName}` : '') + (cadenceStr ? ` · ${cadenceStr}` : '')
    + (taxPackAvailable() ? ` · ${item.tax_deductible ? 'tax-deductible' : 'not tax-deductible'}` : '')].join('');

  // ── writes — every one logs ONE deletable visit ──
  const step = async (d) => {
    try {
      if (d > 0) await api(`/budget/items/${item.id}/log/`, { method: 'POST', body: { units: 1 } });
      else await api(`/budget/items/${item.id}/unlog/`, { method: 'POST', body: { kind: 'unit' } });
    } catch { /* */ }
    refresh();
  };
  const logSpend = async () => {
    const a = Number(String(spendVal).replace(',', '.'));
    if (!(a > 0)) return;
    try { await api(`/budget/items/${item.id}/log/`, { method: 'POST', body: { amount: a } }); } catch { /* */ }
    setSpendVal(''); refresh();
  };
  const justVisited = async () => {
    try { await api('/budget/place-visits/', { method: 'POST', body: { item: item.id, amount: 0, title: item.title, confirmed: true } }); } catch { /* */ }
    refresh();
  };
  const logEvent = async () => {
    if (!event) return;
    try {
      await api('/budget/place-visits/', { method: 'POST', body: {
        item: item.id, place: event.place, title: item.title,
        amount: event.units ? '0.00' : Number(event.amount || 0).toFixed(2),
        units: event.units || 0, rule: event.id, bump: 1, confirmed: true,
      } });
    } catch { /* */ }
    refresh();
  };
  const unlogEvent = async () => {
    if (!eventVisits.length) return;
    const last = eventVisits.reduce((a, b) => (b.id > a.id ? b : a));
    try { await api(`/budget/place-visits/${last.id}/`, { method: 'DELETE' }); } catch { /* */ }
    refresh();
  };
  const delVisit = async (v) => {
    try { await api(`/budget/place-visits/${v.id}/`, { method: 'DELETE' }); } catch { /* */ }
    refresh();
  };
  const setTotal = async () => {
    // An empty box means "no change", never R0: Number('') is 0, and a zero
    // total overrides every logged unit in the budget maths (notes #621/#623).
    const raw = String(totalVal).trim();
    if (raw === '') return;
    const a = Number(raw.replace(',', '.'));
    if (!(a >= 0)) return;
    try { await api(`/budget/items/${item.id}/set_spent/`, { method: 'POST', body: { amount: a } }); } catch { /* */ }
    setTotalVal(''); refresh();
  };
  const patch = async (body) => {
    try { await api(`/budget/items/${item.id}/`, { method: 'PATCH', body }); } catch { /* */ }
    onChange?.();
  };
  const saveAmount = async (raw) => {
    const v = String(raw || '').replace(/[x×]/gi, '*').trim();
    try { await api(`/budget/items/${item.id}/set_amount/`, { method: 'POST', body: { amount: v, scope: 'month' } }); }
    catch { await patch(/[*]/.test(v) ? { calculation: v } : { calculation: '', amount: Number(v) || 0 }); }
    onChange?.();
  };
  const stepCount = (d) => { if (count && rate) saveAmount(`${rate}*${Math.max(1, Math.round(count) + d)}`); };
  const openRecount = async () => {
    try { setRecount(await api(`/budget/items/${item.id}/recount_spend/`, { method: 'POST', body: { dry: 1 } })); }
    catch { setRecount(null); }
  };
  const runRecount = async (mode) => {
    setBusy(true);
    try { await api(`/budget/items/${item.id}/recount_spend/`, { method: 'POST', body: { mode } }); } catch { /* */ }
    setBusy(false); setRecount(null); refresh();
  };
  const doMerge = async () => {
    if (!mergeTo) return;
    setBusy(true);
    try { await api(`/budget/items/${item.id}/merge_into/`, { method: 'POST', body: { target: mergeTo } }); } catch { /* */ }
    setBusy(false); setMergeTo(''); onChange?.(); onClose?.();
  };
  const del = async () => {
    setBusy(true);
    try { await api(`/budget/items/${item.id}/`, { method: 'DELETE' }); } catch { /* */ }
    setBusy(false); onChange?.(); onClose?.();
  };

  const kindLabel = event ? 'MAP EVENT' : (formula ? 'FORMULA' : (isExpense ? 'SPEND-ONLY' : item.kind?.toUpperCase()));
  const mergeTargets = items.filter((i) => i.kind === 'expense' && i.id !== item.id);
  const violet = 'mt-1 mb-2 px-3.5 py-2.5 rounded-xl text-sm leading-snug';
  const violetStyle = { background: 'var(--color-accent-900, #2b2741)', boxShadow: 'inset 0 0 0 1px var(--color-accent-800,#423a6a)', color: 'var(--color-accent-200,#e7e5fe)' };

  return (
    <div className="text-slate-100">
      {/* ── Zone 1 · Header (hidden when embedded under an existing row) ── */}
      {showHeader && (<>
      <div className="flex items-start gap-3">
        <button
          className={`w-6 h-6 mt-1 shrink-0 rounded-full grid place-items-center text-[13px] font-bold border-[1.5px] transition ${item.completed ? 'bg-vblue border-vblue text-ink' : 'border-edge text-transparent hover:border-vblue'}`}
          onClick={() => (onTick ? onTick(item) : patch({ completed: !item.completed }))}
          aria-label={item.completed ? 'mark not done' : 'mark done'}>✓</button>
        <div className="flex-1 min-w-0">
          <input className="w-full bg-transparent border-0 p-0 text-xl font-semibold text-white focus:outline-none"
                 defaultValue={item.title} key={`t-${item.id}`}
                 onBlur={(e) => { if (e.target.value && e.target.value !== item.title) patch({ title: e.target.value }); }} />
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[9px] tracking-[0.11em] px-2 py-0.5 rounded border border-vblue text-vblue">{kindLabel}</span>
          </div>
        </div>
        {isExpense && (
          <div className="text-right shrink-0">
            <div className="text-2xl font-semibold text-white tracking-tight">{fmtR(budget)}</div>
            <div className="text-xs text-slate-500">{item.completed
              ? (over ? `paid · ${fmtR(spent)} spent · ${fmtR(spent - budget)} over`
                 : (left > 0 ? `paid · ${fmtR(spent)} spent · ${fmtR(left)} left over` : `paid in full · ${fmtR(spent)}`))
              : `${fmtR(spent)} spent · ${over ? fmtR(spent - budget) + ' over' : fmtR(left) + ' left'}`}</div>
            {spendBreakdown(item) && <div className="text-[11px] text-slate-600">{spendBreakdown(item)}</div>}
          </div>
        )}
        {onClose && <button className="text-slate-500 hover:text-white text-lg shrink-0 leading-none" onClick={onClose} aria-label="close">×</button>}
      </div>
      {isExpense && budget > 0 && (
        <div className="h-[5px] rounded-full bg-ink overflow-hidden mt-3">
          <div className="h-full rounded-full transition-all" style={{ width: `${budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0}%`, background: over ? 'var(--color-bad, #e2707a)' : 'var(--color-accent, #9184d9)' }} />
        </div>
      )}
      </>)}

      {/* ── Zone 2 · Record what you spent (open) ─────────────────────── */}
      {isExpense && (<>
        <div className="text-[10px] uppercase tracking-[0.14em] text-vblue mt-5 mb-2">Record what you spent</div>

        {/* Formula: violet read-back directly above its stepper. */}
        {formula && (<>
          <div className={violet} style={violetStyle}>{fmtCount(count)} units of {rc(rate)} = {rc(budget)} this month · each ± step pays {rc(rate)}</div>
          <div className="rounded-xl px-3 py-2.5 mb-3 flex items-center gap-3" style={{ boxShadow: 'inset 0 0 0 1px var(--color-divider, #2c2f47)' }}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">{item.reduce_n || 0} of {count ? fmtCount(count) : '—'} units paid</div>
              <div className="text-xs text-slate-500 mt-0.5">{fmtR((item.reduce_n || 0) * rate)} covered so far{count ? ` · ${fmtCount(Math.max(0, count - (item.reduce_n || 0)))} left` : ''}</div>
            </div>
            <button className="w-11 h-11 shrink-0 rounded-xl text-2xl grid place-items-center border border-edge text-slate-200 hover:border-vblue disabled:opacity-30 transition"
                    disabled={!item.reduce_n} onClick={() => step(-1)} aria-label="pay one less unit">−</button>
            <div className="min-w-[34px] text-center text-2xl font-semibold" style={{ color: 'var(--color-accent-300, #d2cefd)' }}>{item.reduce_n || 0}</div>
            <button className="w-11 h-11 shrink-0 rounded-xl text-2xl grid place-items-center text-ink font-medium transition"
                    style={{ background: 'var(--color-accent, #9184d9)' }} onClick={() => step(1)} aria-label="pay one more unit">+</button>
          </div>
        </>)}

        {!formula && !event && (
          <div className={violet} style={violetStyle}>no formula — whatever you log is what it cost</div>
        )}

        {/* Map event: violet read-back directly above its stepper. */}
        {event && (<>
          <div className={violet} style={violetStyle}>
            {event.cadence === 'daily'
              ? `${fmtR(evtPerVisit * evtTimes)} a day, ${evtTimes} ${evtTimes === 1 ? 'visit' : 'visits'}${formula ? ' · logs on top of the units above' : ''}`
              : `${fmtR(evtPerVisit)} each visit${formula ? ' · logs on top of the units above' : ''}`}
          </div>
          <div className="rounded-xl px-3 py-2.5 mb-3 flex items-center gap-3"
               style={{ background: 'var(--color-accent-900, #2b2741)', boxShadow: 'inset 0 0 0 1px var(--color-accent-800, #423a6a)' }}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--color-accent-200, #e7e5fe)' }}>📍 {placeName || 'the event'}</div>
              <div className="text-xs mt-0.5" style={{ color: 'color-mix(in srgb, var(--color-accent-200, #e7e5fe) 60%, transparent)' }}>{fmtR(evtPerVisit)} each · {fmtR(eventLogged)} logged</div>
            </div>
            <button className="w-11 h-11 shrink-0 rounded-xl text-2xl grid place-items-center border border-edge text-slate-200 hover:border-vblue disabled:opacity-30 transition"
                    disabled={!eventVisits.length} onClick={unlogEvent} aria-label="reverse the last event visit">−</button>
            <div className="text-center leading-none">
              <div className="min-w-[34px] text-2xl font-semibold" style={{ color: 'var(--color-accent-300, #d2cefd)' }}>{evtToday}</div>
              <div className="text-[9px] text-slate-500 mt-0.5">today</div>
            </div>
            <button className="w-11 h-11 shrink-0 rounded-xl text-2xl grid place-items-center text-ink font-medium transition"
                    style={{ background: 'var(--color-accent, #9184d9)' }} onClick={logEvent} aria-label="log one event visit">+</button>
          </div>
        </>)}

        <div className="flex gap-2 items-center flex-wrap">
          <input className="input flex-1 min-w-[140px] !py-2.5 text-sm" inputMode="decimal" placeholder="Log a rand amount"
                 value={spendVal} onChange={(e) => setSpendVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && logSpend()} />
          <VoiceButton size={44} onText={(t) => setSpendVal(String(t).replace(/[^\d.,]/g, ''))} />
          <button className="btn-primary !py-2.5 !px-4 text-sm" onClick={logSpend}>Log spend</button>
          <button className="rounded-xl border border-edge px-3.5 py-2.5 text-sm hover:border-vblue transition" onClick={justVisited}>✓ Just visited</button>
        </div>
        {onScan && (
          <div className="flex gap-2 flex-wrap mt-2">
            <button className="flex-1 min-w-[120px] rounded-xl border border-edge px-3 py-2 text-sm hover:border-vblue transition" onClick={() => onScan(item)}>📷 Scan a receipt</button>
          </div>
        )}
      </>)}

      {/* ── Zone 3 · Set up as (collapsed one line + Change) ──────────── */}
      <div className="flex items-center gap-2 mt-4 rounded-xl border border-edge px-3 py-2.5" style={{ background: 'var(--color-surface, #1c1f31)' }}>
        <div className="flex-1 min-w-0 text-xs text-slate-400 truncate">{setupLine}</div>
        <button className="text-xs text-vblue hover:text-white shrink-0" onClick={() => setSetupOpen((v) => !v)}>{setupOpen ? 'Done' : 'Change'}</button>
      </div>

      {isExpense && (
        <div className="mt-2 rounded-xl border border-edge px-3 py-2.5" style={{ background: 'var(--color-surface, #1c1f31)' }}>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isRecurring || recurOpen} disabled={isRecurring || recurBusy}
                   onChange={(e) => setRecurOpen(e.target.checked)} />
            <span className={isRecurring ? 'text-vpurple' : ''}>{isRecurring ? '🔁 Recurring — in every month ✓' : '🔁 Make recurring — every month'}</span>
          </label>
          {!isRecurring && recurOpen && (
            <div className="mt-2 rounded-xl border border-vpurple/30 bg-vpurple/5 p-3 space-y-3">
              <div className="text-[11px] uppercase tracking-wide text-vpurple">⚙ Advanced repeat</div>
              <div className="flex flex-wrap gap-1.5">
                {[['none', 'One-off'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setRcad(v)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition ${rcad === v ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400 hover:border-slate-400'}`}>
                    {l}
                  </button>
                ))}
              </div>
              {rcad !== 'none' && (
                <label className="block text-xs text-slate-400">R per unit
                  <input className="input mt-1 !py-2 text-sm" type="number" inputMode="decimal" placeholder="e.g. 200"
                         value={rUnit} onChange={(e) => setRUnit(e.target.value)} />
                </label>
              )}
              {rcad === 'daily' && (
                <label className="block text-xs text-slate-400">Times per day
                  <input className="input mt-1 !py-2 text-sm" type="number" min="1" max="12"
                         value={rTimes} onChange={(e) => setRTimes(e.target.value)} />
                </label>
              )}
              {rcad === 'weekly' && (
                <div>
                  <div className="text-xs text-slate-400 mb-1.5">On these days</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']].map(([d, l]) => {
                      const on = rDow.includes(d);
                      return (
                        <button key={d} type="button"
                                onClick={() => setRDow((sd) => on ? sd.filter((x) => x !== d) : [...sd, d])}
                                className={`text-xs px-2.5 py-1.5 rounded-lg border transition ${on ? 'border-vpurple text-vpurple bg-vpurple/10' : 'border-edge text-slate-400 hover:border-slate-400'}`}>
                          {l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {rcad === 'monthly' && (
                <label className="block text-xs text-slate-400">Day of month (optional)
                  <input className="input mt-1 !py-2 text-sm" type="number" min="1" max="28" placeholder="e.g. 1"
                         value={rDay} onChange={(e) => setRDay(e.target.value)} />
                </label>
              )}
              <button className="text-xs py-2 px-3 rounded-xl border border-vpurple text-vpurple bg-vpurple/10 disabled:opacity-40"
                      disabled={recurBusy} onClick={makeRecurring}>
                {recurBusy ? 'Saving…' : 'Add to recurring'}
              </button>
            </div>
          )}
        </div>
      )}

      {setupOpen && (
        <div className="mt-2 space-y-3 rounded-xl border border-edge p-3" style={{ background: 'var(--color-surface, #1c1f31)' }}>
          <div className="flex gap-2 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[11px] text-slate-500 mb-1">{formula ? 'Amount or formula — unit × count' : 'Amount each time'}</label>
              <input className="input !py-2 text-sm" key={`amt-${item.id}`} defaultValue={item.calculation || item.amount}
                     onBlur={(e) => { const v = e.target.value; if (v !== (item.calculation || String(item.amount))) saveAmount(v); }} />
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="block text-[11px] text-slate-500 mb-1">Category</label>
              <select className="input !py-2 text-sm" defaultValue={item.category || ''} key={`cat-${item.id}`}
                      onChange={(e) => patch({ category: e.target.value || null })}>
                <option value="">—</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="block text-[11px] text-slate-500 mb-1">Paid from</label>
              <select className="input !py-2 text-sm" defaultValue={item.bank || ''} key={`bank-${item.id}`}
                      onChange={(e) => patch({ bank: e.target.value || null })}>
                <option value="">—</option>
                {banks.map((b) => <option key={b.id} value={b.id}>{b.account_name ? `${b.name} \u00b7 ${b.account_name}` : b.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="flex-1 min-w-0 truncate text-slate-300">{placeName ? `📍 ${placeName}` : 'No place linked'}</span>
            {onOpenMap && <button className="text-vblue hover:text-white shrink-0 text-xs" onClick={() => onOpenMap(item)}>{placeName ? 'Move pin / change ▸' : '+ Link a place ▸'}</button>}
          </div>

          {taxPackAvailable() && <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" defaultChecked={!!item.tax_deductible} key={`tax-${item.id}`} onChange={(e) => patch({ tax_deductible: e.target.checked })} />
            Tax-deductible (SARS)
          </label>}

          <button className="text-xs text-slate-400 hover:text-white transition" onClick={() => setMoreOpen((v) => !v)}>{moreOpen ? 'Fewer options' : 'More options'}</button>

          {moreOpen && (
            <div className="space-y-3 pt-1">
              <div className="flex gap-2 flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-[11px] text-slate-500 mb-1">Set the total spent — overrides everything</label>
                  <div className="flex gap-2">
                    <input className="input flex-1 !py-2 text-sm" inputMode="decimal" placeholder={fmtR(spent)} value={totalVal} onChange={(e) => setTotalVal(e.target.value)} />
                    <button className="rounded-xl border border-amber-400/50 text-amber-400 !px-3 text-sm hover:bg-amber-400/10 transition" onClick={setTotal}>Set</button>
                  </div>
                </div>
                {formula && (
                  <div className="w-[130px]">
                    <label className="block text-[11px] text-slate-500 mb-1">Budget count</label>
                    <div className="flex items-center gap-2 min-h-[38px]">
                      <button className="w-8 h-8 rounded-lg border border-edge hover:border-vblue" onClick={() => stepCount(-1)}>−</button>
                      <span className="flex-1 text-center font-semibold">{count ? fmtCount(count) : '—'}</span>
                      <button className="w-8 h-8 rounded-lg border border-edge hover:border-vblue" onClick={() => stepCount(1)}>+</button>
                    </div>
                  </div>
                )}
              </div>
              {taxPackAvailable() && <select className="input !w-auto !py-1.5 text-sm" defaultValue={item.tax_bucket || ''} key={`bkt-${item.id}`} onChange={(e) => patch({ tax_bucket: e.target.value })}>
                <option value="">tax bucket…</option>
                {BUCKETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>}
              {mergeTargets.length > 0 && (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="block text-[11px] text-slate-500 mb-1">Merge into another expense</label>
                    <select className="input !py-2 text-sm" value={mergeTo} onChange={(e) => setMergeTo(e.target.value)}>
                      <option value="">choose an expense…</option>
                      {mergeTargets.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
                    </select>
                  </div>
                  <button className="rounded-xl border border-edge px-3 py-2 text-sm hover:border-vblue transition disabled:opacity-40" disabled={!mergeTo || busy} onClick={doMerge}>Merge</button>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <button className="rounded-lg border border-edge px-3 py-1.5 text-sm hover:border-vblue transition" onClick={openRecount}>Recount from logged</button>
                {isExpense && (isPaidAhead
                  ? <button className="rounded-lg border border-edge px-3 py-1.5 text-sm hover:border-vblue transition" disabled={busy} onClick={() => payAhead(true)}>Undo paid ahead</button>
                  : <button className="rounded-lg border border-edge px-3 py-1.5 text-sm hover:border-vblue transition" disabled={busy} onClick={() => payAhead(false)}
                            title="Paid this twice by mistake? Next month's line is marked paid and nothing more leaves your bank then.">Paid twice? Count it for next month</button>)}
                <button className="rounded-lg px-3 py-1.5 text-sm transition" style={{ color: 'var(--color-bad, #e2707a)' }} onClick={del} disabled={busy}>Delete</button>
              </div>
              {aheadMsg && <div className="text-xs text-vpurple">{aheadMsg}</div>}
              {recount && (
                <div className="rounded-xl border border-edge p-3 text-sm" style={{ background: 'var(--color-surface-2, #23263a)' }}>
                  <div className="text-slate-300 mb-2">Recount from your logged items:</div>
                  <div className="text-xs text-slate-400 space-y-1 mb-3">
                    <div>Now: <b className="text-white">{fmtR(spent)}</b></div>
                    <div>Clear to logs → <b className="text-white">{fmtR(recount.spent_logs ?? spent)}</b></div>
                    {recount.has_untracked && <div>Log the missing → <b className="text-white">{fmtR(recount.spent_backfill ?? spent)}</b></div>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {recount.has_untracked && <button className="btn-primary !py-1.5 !px-3 text-sm" disabled={busy} onClick={() => runRecount('backfill')}>Log the missing</button>}
                    <button className="rounded-lg border border-edge px-3 py-1.5 text-sm hover:border-vblue transition" disabled={busy} onClick={() => runRecount('logs')}>Clear to logs</button>
                    <button className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:text-white" onClick={() => setRecount(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Zone 4 · Logged this month (collapsed one line + Show) ────── */}
      <div className="flex items-center gap-2 mt-2 rounded-xl border border-edge px-3 py-2.5" style={{ background: 'var(--color-surface, #1c1f31)' }}>
        <div className="flex-1 min-w-0 text-xs text-slate-400 truncate">
          Logged this month · {visits.length} {visits.length === 1 ? 'entry' : 'entries'} · {fmtR(logTotal)}
          {receiptCount ? ` · ${receiptCount} with a receipt` : ''}
          {lastOn ? ` · last ${shortDate(lastOn)}` : ''}
        </div>
        <button className="text-xs text-vblue hover:text-white shrink-0" onClick={() => setLoggedOpen((v) => !v)}>{loggedOpen ? 'Hide' : 'Show'}</button>
      </div>

      {loggedOpen && (
        <div className="mt-2 rounded-xl border border-edge overflow-hidden">
          <div className="max-h-[196px] overflow-y-auto divide-y divide-edge/60">
            {visits.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-500">Nothing logged yet this month.</div>}
            {visits.map((v) => {
              const money = visMoney(v);
              const kind = v.units > 0 ? '±1' : (money ? 'R' : '👣');
              return (
                <div key={v.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                  <span className={`w-8 h-8 shrink-0 rounded-lg grid place-items-center text-[11px] font-semibold border ${money || v.units ? 'border-accent2 text-vpurple' : 'border-edge text-slate-500'}`}>{kind}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-200 truncate">{v.units > 0 ? `${v.units} unit${v.units > 1 ? 's' : ''}` : (money ? 'Spend' : 'Visited, spent nothing')}</div>
                    <div className="text-[11px] text-slate-500 truncate">{[shortDate(v.on), v.place_name && `📍 ${v.place_name}`].filter(Boolean).join(' · ')}</div>
                  </div>
                  {onScan && <button className="w-8 h-8 shrink-0 rounded-lg border text-sm" style={{ borderColor: 'var(--color-accent-700,#5d5294)', color: 'var(--color-accent-300,#d2cefd)' }} onClick={() => onScan(item, v)} title={v.receipt ? 'Receipt attached — tap to replace' : 'Add a receipt to this logged line'} aria-label="receipt">{v.receipt ? '🧾' : '📎'}</button>}
                  <span className={`shrink-0 tabular-nums ${money ? 'text-slate-200' : 'text-slate-500'}`}>{fmtR(money)}</span>
                  <button className="shrink-0 text-slate-500 hover:text-red-400" onClick={() => delVisit(v)} aria-label="delete">×</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
